
import { Component, Vue } from 'vue-property-decorator';

import { sharedState } from '../../../../../framework/state';
import DateField from '../../../../../framework/fields/DateField.vue';

import { SortOrder } from '../../../../../../common/framework/model/SortOrder';
import { FrameworkUserRole } from '../../../../../../common/framework/enumeration/FrameworkUserRole';
import {
    getEnumerationOptions,
    getOptionLabel,
    getResourceOptions,
    OptionValue,
} from '../../../../../framework/service/options';
import { getResourcePagingNew } from '../../../../../framework/client/resource';

import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleState } from '../../../../../../common/application/model/enums/VehicleState';
import { PageSize } from '../../../../../../common/application/model/enums/PageSize';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import { VehicleType } from '../../../../../../common/application/model/enums/VehicleType';

import {
    getVehicleContracts,
    updateVehicleInspectionDates,
} from '../../../../../application/client/vehicle_contract_client';
import { getTagTypeFromVehicleState } from '../../../../service/tag_type_service';
import { getCustomerOptions } from '../../../../../application/service/application_options_service';
import { errorToast } from '../../../../../application/service/toast_service';
import ParameterService from '../../../../../application/service/parameter_service';

@Component({
    components: { DateField },
})
export default class VehicleContractInspectionDates extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as VehicleContract[],
        loading: false,
        parameters: this.parameterService.get(),
        total: 0,
        vehicleStateOptions: [] as OptionValue[],
        customerOptions: [] as OptionValue[],
        vehicleMakeOptions: [] as OptionValue[],
        pageSizeOptions: [] as number[],
    };

    async mounted() {
        await this.loadAsyncData();
        this.local.vehicleMakeOptions = await getResourceOptions(ApplicationResource.VEHICLE_MAKE, 'name');
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);
        this.local.pageSizeOptions = Object.values(PageSize).filter((o) => typeof (o as any) === 'number') as number[];
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getVehicleMakeLabel(id: string): string {
        return getOptionLabel(id, this.local.vehicleMakeOptions);
    }

    getVehicleState(id: string): string {
        return getOptionLabel(id, this.local.vehicleStateOptions);
    }

    getTagType(id: VehicleState): string {
        return getTagTypeFromVehicleState(id);
    }

    async onSortChange(field: string, order: SortOrder) {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    async saveDates(): Promise<void> {
        try {
            const result = await updateVehicleInspectionDates(this.local.rows);

            this.$buefy.toast.open({
                message: this.$t('message.updateVehicleInspectionDates', [result.updated]).toString(),
                type: 'is-success',
                position: 'is-top',
                duration: 8000,
            });
        } catch (e) {
            errorToast(e);
        }
    }

    back() {
        this.$router.go(-1);
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        const customPart = backendParameters.get('custom');
        backendParameters.delete('custom');

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters, customPart)).rowCount;
        const contractResult = await getVehicleContracts(backendParameters, customPart);
        this.local.rows = contractResult.vehicleContracts.filter(
            ({ vehicleType }) =>
                vehicleType !== VehicleType.BICYCLE &&
                vehicleType !== VehicleType.UNSPECIFIED &&
                vehicleType !== VehicleType.O1 &&
                !!vehicleType,
        );
        this.local.customerOptions = await getCustomerOptions();

        this.local.loading = false;
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
